import { z } from "zod";

import {
  VibeAppHealthCheckConfigSchema,
  VibeAppResourceQuotasSchema,
  VibeAppVisibilitySchema
} from "../../../../schemas/VibeApp";
import { VibeAppDeployabilityReadSchema } from "../../../../shared/domain/vibe/app-deployability";
import { VibeBuildComputeSizeSchema } from "../../../../shared/domain/vibe/build-compute-size";
import { VibeShipGateModeSchema } from "../../../../shared/domain/vibe/ship-gate-mode";
import { VibeAppGitProjectSummarySchema } from "./app-summaries.schemas";
import {
  VibeAppDescriptionSchema,
  VibeAppNameSchema,
  VibeAppSchema,
  VibeDeployBranchSchema
} from "./core";
import { VibeDeploymentSchema } from "./deployments.schemas";
import { VibeTemplateSlugSchema } from "./templates.schemas";

export * from "./app-deployment-history.schemas";
export * from "./app-deployment-summary.schemas";
export * from "./app-icon.schemas";
export * from "./app-list.schemas";
export * from "./app-summaries.schemas";

// Create app
export const CreateVibeAppBodySchema = z.object({
  name: VibeAppNameSchema,
  description: VibeAppDescriptionSchema.optional(),
  /// Reachability of the deployed app. `PUBLIC` skips the per-app edge-auth
  /// token so the URL is browser-reachable; `PRIVATE` (default) mints the token
  /// so the app is agent-tool-only. Omitted ⇒ PRIVATE (safe by default).
  visibility: VibeAppVisibilitySchema.optional(),
  /**
   * Published template to start from. When set, the backend seeds the
   * template's default env vars onto the new app; the starter code
   * itself arrives with the user's first push (the setup checklist
   * surfaces the clone command). Unknown/unpublished slug → 404 and
   * no app is created.
   */
  templateSlug: VibeTemplateSlugSchema.optional()
});

export const CreateVibeAppResponseSchema = z.object({
  app: VibeAppSchema,
  /**
   * A live git project in the org that ALREADY goes by this app's name, or
   * `null` — the usual case.
   *
   * A warning, not a failure: the app is created either way, and the response
   * is a 201. It exists because `provision-repo` mints a project named after
   * the app, so a name already held will 409 that verb later, and the operator
   * currently meets that 409 with no memory of where the collision came from.
   *
   * `attach-repo <appId> <thisProjectId>` is the way through, and it usually
   * IS the intent — creating the code store first and the app second is a
   * legitimate ordering. Clients should surface this as an actionable notice
   * next to the created app, never as an error.
   *
   * `nullish`, not `nullable`, for the same deploy-skew reason as `icon` on
   * `VibeAppSchema`: the console (Vercel) and the backend (Porter) ship
   * independently, so a frontend one release ahead parses a create response
   * that omits this key. A required key turns that window into a whole-response
   * parse failure AFTER the app is already written — and the retry then 409s on
   * the app name, which is the exact dead end this field exists to prevent.
   * Absent and `null` mean the same thing here, so accept both.
   */
  gitProjectNameCollision: VibeAppGitProjectSummarySchema.nullish()
});

// Get app
export const GetVibeAppPathVarsSchema = z.object({
  id: z.string().uuid()
});

export const GetVibeAppResponseSchema = z.object({
  app: VibeAppSchema,
  /**
   * Whether this app can deploy right now, and if not, the one thing in the
   * way. Derived at read time from the git project that resolves for the app
   * — there is no column, so it cannot go stale.
   *
   * Sits BESIDE the app rather than on `VibeAppSchema` because it is not app
   * state: it is a join, exactly like `latestDeployment` on the list read.
   * Putting it on the app schema would oblige every producer of a `VibeApp`
   * — including create, which has no project yet — to resolve one.
   */
  deployability: VibeAppDeployabilityReadSchema,
  /**
   * The git project attached to this app, or `null` (unlinked — every app is
   * born this way; attaching one is a separate action). Lets the console's
   * Code tab route itself without a second round-trip, and carries the
   * git-project id the tree/blob/branches endpoints are keyed by.
   */
  gitProject: VibeAppGitProjectSummarySchema.nullable()
});

// Update app — partial update, only the fields callers actually want
// to change. Every field is optional; the use case treats `undefined`
// as "leave alone" so the caller never has to re-send the entire app.
export const UpdateVibeAppPathVarsSchema = z.object({
  id: z.string().uuid()
});

export const UpdateVibeAppBodySchema = z
  .object({
    description: VibeAppDescriptionSchema.nullable().optional(),
    requireApprovals: z.boolean().optional(),
    /// Set how hard the server-side ship gate applies to this app — `OFF`,
    /// `WARN` or `ENFORCE`. `undefined` leaves it unchanged.
    ///
    /// `WARN` reads the repository and records what it found without stopping
    /// the deploy, which is the on-ramp to `ENFORCE`. `ENFORCE` refuses deploys
    /// whose declared verification artifacts are missing or red at the deployed
    /// commit; `nexus vibe deploy --skip-verification` is the audited override.
    shipGateMode: VibeShipGateModeSchema.optional(),
    /// The pre-mode boolean, still accepted so a published CLI's
    /// `--require-verification` flag keeps working. `true` maps to `ENFORCE`,
    /// `false` to `OFF` — exactly what it always meant.
    ///
    /// A body carrying BOTH this and `shipGateMode` has sent a contradiction it
    /// authored, and `shipGateMode` wins: it is the only one of the two that can
    /// express every state, so honouring the boolean instead would make `WARN`
    /// unreachable for any client that sends both. The controller resolves it.
    requireVerification: z.boolean().optional(),
    /// Change the branch whose pushes deploy (plain branch name, e.g.
    /// `main` → `release/prod`). `undefined` leaves it unchanged.
    deployBranch: VibeDeployBranchSchema.optional(),
    /// Full quotas object — partial-update at the field level lands as
    /// a separate slice if/when ergonomics demand. At v1 the admin
    /// PATCHes the whole `{ cpuMhz, memoryMiB, maxInstances }` so the
    /// Zod schema fully validates the new state in one shot.
    resourceQuotas: VibeAppResourceQuotasSchema.optional(),
    /// Set the compute this app's image builds on — `MEDIUM` or `LARGE`.
    /// `undefined` leaves it unchanged. Takes effect from the next build
    /// admitted; a build already in flight keeps the size it was admitted on.
    buildComputeSize: VibeBuildComputeSizeSchema.optional(),
    /// Full health-check policy — like quotas, the admin PATCHes the
    /// whole `{ path, port, …thresholds }` so the Zod schema fully
    /// validates the new state in one shot.
    healthCheckConfig: VibeAppHealthCheckConfigSchema.optional()
  })
  // Reject empty bodies — every PATCH should change at least one field.
  // Without this an empty `{}` round-trips silently as a no-op, hiding
  // client bugs.
  .refine((b) => Object.values(b).some((v) => v !== undefined), {
    message: "at least one field must be provided"
  });

export const UpdateVibeAppResponseSchema = z.object({
  app: VibeAppSchema
});

// Delete app — soft delete.
//
// Stamps `deletedAt`, so the app immediately drops out of every read and
// the agent desired-state projection (all exclude soft-deleted rows) — its
// running deployment is torn down on the agent's next pass. The row + its
// deployments / audit history stay for recoverability. 404 when the app is
// not in the caller's org or is already deleted (no leaked deletion signal).
export const DeleteVibeAppPathVarsSchema = z.object({
  id: z.string().uuid()
});

export const DeleteVibeAppResponseSchema = z.object({
  deletedId: z.string().uuid()
});

// Rollback app — re-deploy a previous healthy version.
//
// Supersedes the currently-serving HEALTHY deployment and re-activates a
// SUPERSEDED predecessor (SUPERSEDED → DEPLOYING) in one atomic TX, so the
// app rolls back onto that version. Non-destructive: it ADDS a rollout (the
// restored deployment) — nothing is deleted, and the old version keeps
// serving until the restored one reports healthy (blue-green). 409 when
// there is nothing to roll back to (no live version, no previous version,
// the named target is already serving, or a deploy already in flight).
export const RollbackVibeAppPathVarsSchema = z.object({
  id: z.string().uuid()
});

// `targetDeploymentId` names which SUPERSEDED deployment to restore.
// Optional, and deliberately so: an older org API key or CLI predating
// NEX-3083 that omits it keeps today's behavior — restore the single
// most-recent SUPERSEDED predecessor. The console always sends it now
// (one "Roll back to this" per row instead of the old header control).
//
// `.default({})` on the OBJECT itself, not just the field — a caller that
// predates this change may send no body at all (no Content-Type, no
// payload), which reaches the controller as `undefined`, not `{}`. Zod
// requires `parse()`'s top-level input to actually BE an object even when
// every field is optional — `z.object({ x: z.string().optional() })
// .parse(undefined)` throws "expected object, received undefined". Without
// this default, the exact back-compat path the field comment above
// describes was itself unreachable for the caller it exists to serve —
// found by bugbot on PR #2253, verified against a real Zod parse before
// trusting the report.
export const RollbackVibeAppBodySchema = z
  .object({
    targetDeploymentId: z.string().uuid().optional()
  })
  .default({});

export const RollbackVibeAppResponseSchema = z.object({
  /// The predecessor re-activated into DEPLOYING — the version the app is
  /// rolling back to. The agent re-places its retained image.
  restoredDeployment: VibeDeploymentSchema,
  /// The previously-serving deployment retired into SUPERSEDED.
  supersededDeployment: VibeDeploymentSchema
});
